import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Buildings, Trash, PencilSimple, UploadSimple, Key, Copy, Check } from "@phosphor-icons/react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { COUNTRIES as _COUNTRIES, COUNTRY_BY_CODE as _COUNTRY_BY_CODE } from "@/lib/countries";

const EMPTY = { name: "", contact_email: "", phone: "", address: "", country: "JM", website: "", plan: "starter" };

// Filter out "OTHER" for the country picker in the company form (ISO-2 required)
const COUNTRY_OPTIONS = _COUNTRIES.filter((c) => c.code !== "OTHER");
const COUNTRY_BY_CODE = _COUNTRY_BY_CODE;

function groupByCountry(rows) {
  // Returns [[countryCode, [companies]], ...] sorted by country name (JM first).
  const map = {};
  rows.forEach((r) => {
    const code = (r.country || "JM").toUpperCase();
    (map[code] = map[code] || []).push(r);
  });
  const entries = Object.entries(map);
  entries.sort(([a], [b]) => {
    if (a === "JM") return -1;
    if (b === "JM") return 1;
    const na = (COUNTRY_BY_CODE[a] || {}).name || a;
    const nb = (COUNTRY_BY_CODE[b] || {}).name || b;
    return na.localeCompare(nb);
  });
  return entries;
}

const PLAN_COLORS = {
  starter: "bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700",
  pro: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20",
  scale: "bg-[#002BF6]/10 text-[#002BF6] border-[#002BF6]/20",
};

export default function Companies() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const dialogLogoRef = React.useRef(null);

  const load = async () => {
    const { data } = await api.get("/companies");
    setRows(data);
  };

  useEffect(() => { load(); }, []);

  const pickLogo = (file) => {
    if (!file) { setLogoFile(null); setLogoPreview(null); return; }
    setLogoFile(file);
    const r = new FileReader();
    r.onload = () => setLogoPreview(r.result);
    r.readAsDataURL(file);
  };

  const save = async () => {
    try {
      let companyId = editing?.id;
      if (editing) {
        await api.patch(`/companies/${editing.id}`, form);
      } else {
        const { data } = await api.post("/companies", form);
        companyId = data.id;
      }
      if (logoFile && companyId) {
        const fd = new FormData();
        fd.append("file", logoFile);
        try {
          await api.post(`/companies/${companyId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) {
          toast.error(`Logo upload failed: ${formatApiErrorDetail(e.response?.data?.detail)}`);
        }
      }
      toast.success(editing ? "Company updated" : "Company created");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      setLogoFile(null);
      setLogoPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this company and all its data? This cannot be undone.")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Company deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const edit = (c) => {
    setEditing(c);
    setForm({ name: c.name, contact_email: c.contact_email, phone: c.phone || "", address: c.address || "", country: c.country || "JM", website: c.website || "", plan: c.plan || "starter" });
    setOpen(true);
  };

  const uploadLogo = async (companyId, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/companies/${companyId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="companies-page">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
            Tenant Management
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>
            Courier Companies
          </h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); setLogoFile(null); setLogoPreview(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="add-company-btn">
              <Plus size={16} className="mr-1.5" weight="bold" />
              New Company
            </Button>
          </DialogTrigger>
          <DialogContent
            className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
            data-testid="company-dialog"
          >
            <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
              <DialogTitle>{editing ? "Edit Company" : "Create Company"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto px-5 sm:px-6 py-4 flex-1" data-testid="company-dialog-body">
              {/* Logo uploader */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Courier logo</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-md border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden" data-testid="logo-preview">
                    {logoPreview ? (
                      <img src={logoPreview} alt="preview" className="w-full h-full object-cover" />
                    ) : editing?.logo_path ? (
                      <ExistingLogo path={editing.logo_path} />
                    ) : (
                      <Buildings size={22} className="text-zinc-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      ref={dialogLogoRef}
                      className="hidden"
                      onChange={(e) => pickLogo(e.target.files?.[0] || null)}
                      data-testid="dialog-logo-input"
                    />
                    <Button type="button" variant="outline" onClick={() => dialogLogoRef.current?.click()} data-testid="dialog-logo-btn">
                      <UploadSimple size={14} className="mr-1.5" />
                      {logoFile ? "Change image" : "Upload logo"}
                    </Button>
                    <div className="text-[11px] text-zinc-500 mt-1">PNG, JPG, WEBP or SVG. Shown on invoices (Scale plan).</div>
                  </div>
                </div>
              </div>
              <Field label="Company name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="company-name" />
              <Field label="Contact email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testId="company-email" />
              <Field label="Contact phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="company-phone" />
              <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} testId="company-website" />
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Subscription plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="company-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter" data-testid="plan-option-starter">Starter — $35/mo</SelectItem>
                    <SelectItem value="pro" data-testid="plan-option-pro">Pro — $350/mo</SelectItem>
                    <SelectItem value="scale" data-testid="plan-option-scale">Scale — $750/mo</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-zinc-500 mt-1">Sets the tenant's plan on creation. Stripe billing can be initiated later from /billing.</div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Country</Label>
                <Select value={form.country || "JM"} onValueChange={(v) => setForm({ ...form, country: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="company-country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`country-option-${c.code}`}>
                        <span className="mr-1.5">{c.flag}</span> {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-zinc-500 mt-1">Used to group companies on the super-admin list.</div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Address</Label>
                <div className="mt-1.5">
                  <AddressAutocomplete
                    value={form.address}
                    onChange={(v) => setForm({ ...form, address: v })}
                    dataTestId="company-address"
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-company-btn">
                {editing ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <OnboardingStatusPanel />

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="companies-table">
          <thead className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-800">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
              <th className="px-5 py-3 font-semibold">Company</th>
              <th className="px-5 py-3 font-semibold">Contact</th>
              <th className="px-5 py-3 font-semibold">Address</th>
              <th className="px-5 py-3 font-semibold">Website</th>
              <th className="px-5 py-3 font-semibold">Plan</th>
              <th className="px-5 py-3 font-semibold">Created</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500">No companies yet.</td></tr>
            ) : groupByCountry(rows).map(([code, group]) => (
              <React.Fragment key={code}>
                <tr className="bg-zinc-50/80 dark:bg-zinc-800/40 border-b border-zinc-200 dark:border-zinc-800" data-testid={`country-group-${code}`}>
                  <td colSpan={7} className="px-5 py-2.5">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-600 dark:text-zinc-300">
                      <span className="text-base leading-none">{(COUNTRY_BY_CODE[code] || {}).flag || "🌐"}</span>
                      <span>{(COUNTRY_BY_CODE[code] || {}).name || code}</span>
                      <span className="text-zinc-400 normal-case tracking-normal font-normal">· {group.length} {group.length === 1 ? "company" : "companies"}</span>
                    </div>
                  </td>
                </tr>
                {group.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-800/40" data-testid={`company-row-${c.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <CompanyLogo company={c} />
                        <div>
                          <div className="font-medium text-zinc-900 dark:text-zinc-100">{c.name}</div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400">{c.phone || ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-zinc-700 dark:text-zinc-300">{c.contact_email}</td>
                    <td className="px-5 py-4 text-zinc-700 dark:text-zinc-300 max-w-[260px]" data-testid={`company-address-${c.id}`}>
                      {c.address ? (
                        <span className="text-xs leading-snug" title={c.address}>{c.address}</span>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-zinc-700 dark:text-zinc-300">
                      {c.website ? <a href={c.website} target="_blank" rel="noreferrer" className="text-[#002BF6] hover:underline">{c.website.replace(/^https?:\/\//, '')}</a> : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold border ${PLAN_COLORS[c.plan] || PLAN_COLORS.starter}`} data-testid={`plan-${c.id}`}>
                        {(c.plan || "starter").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-zinc-500 dark:text-zinc-400 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-right">
                      <LogoUploadButton companyId={c.id} onUpload={uploadLogo} />
                      <PartnerKeysButton company={c} />
                      <Button variant="ghost" size="sm" onClick={() => edit(c)} data-testid={`edit-${c.id}`}>
                        <PencilSimple size={15} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(c.id)} data-testid={`delete-${c.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash size={15} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompanyLogo({ company }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let url;
    if (!company.logo_path) { setSrc(null); return; }
    (async () => {
      try {
        const resp = await api.get(`/files/${company.logo_path}`, { responseType: "blob" });
        url = URL.createObjectURL(resp.data);
        setSrc(url);
      } catch (e) { setSrc(null); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [company.logo_path]);
  if (src) return <img src={src} alt={company.name} className="w-10 h-10 rounded-md object-cover border border-zinc-200 dark:border-zinc-800 bg-white" />;
  return (
    <div className="w-10 h-10 rounded-md bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-700 dark:text-zinc-300">
      <Buildings size={18} />
    </div>
  );
}

function LogoUploadButton({ companyId, onUpload }) {
  const ref = React.useRef(null);
  return (
    <>
      <input type="file" ref={ref} accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(companyId, e.target.files[0])} data-testid={`logo-input-${companyId}`} />
      <Button variant="ghost" size="sm" onClick={() => ref.current?.click()} data-testid={`logo-btn-${companyId}`} title="Upload logo (Pro+)">
        <UploadSimple size={15} />
      </Button>
    </>
  );
}

function ExistingLogo({ path }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let url;
    (async () => {
      try {
        const resp = await api.get(`/files/${path}`, { responseType: "blob" });
        url = URL.createObjectURL(resp.data);
        setSrc(url);
      } catch (e) { setSrc(null); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <Buildings size={22} className="text-zinc-400" />;
  return <img src={src} alt="logo" className="w-full h-full object-cover" />;
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
      <Input className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}


function OnboardingStatusPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/onboarding-status");
      setData(d);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resend = async (companyId, companyName) => {
    if (!confirm(`Resend onboarding email for "${companyName}"? Any existing temp password will be invalidated.`)) return;
    setBusyId(companyId);
    try {
      await api.post(`/admin/companies/${companyId}/resend-onboarding`);
      toast.success(`Verification email re-sent to ${companyName}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resend failed");
    } finally {
      setBusyId(null);
    }
  };

  const backfill = async () => {
    if (!confirm("Re-provision ALL unverified company admins with fresh temp passwords + verification emails. Old passwords become invalid. Continue?")) return;
    setBackfilling(true);
    try {
      const { data: r } = await api.post("/admin/backfill-onboarding");
      toast.success(`Sent ${r.sent} · skipped ${r.skipped_already_verified}`);
      load();
    } catch (e) {
      toast.error("Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (loading || !data) return null;
  const t = data.totals || {};
  const allVerified = t.unverified === 0 && t.no_admin === 0;

  return (
    <div className="mb-6 bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="onboarding-status-panel">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-100 bg-zinc-50">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
            Onboarding status
          </div>
          <div className="text-sm text-zinc-700 mt-0.5">
            <span className="font-semibold">{t.total}</span> total ·{" "}
            <span className="text-emerald-700 font-semibold">{t.verified}</span> verified ·{" "}
            <span className={`font-semibold ${t.unverified ? "text-amber-700" : "text-zinc-500"}`}>{t.unverified}</span> unverified
            {t.no_admin ? <> · <span className="text-red-700 font-semibold">{t.no_admin}</span> missing admin</> : null}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={backfill}
          disabled={backfilling || allVerified}
          data-testid="backfill-onboarding-btn"
        >
          {backfilling ? "Re-sending…" : "Resend all unverified"}
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-white border-b border-zinc-100">
          <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
            <th className="px-5 py-2 font-semibold">Company</th>
            <th className="px-5 py-2 font-semibold">Admin email</th>
            <th className="px-5 py-2 font-semibold">Status</th>
            <th className="px-5 py-2 font-semibold text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.company_id} className="border-b border-zinc-100 last:border-0" data-testid={`onboarding-row-${r.company_id}`}>
              <td className="px-5 py-2.5 font-medium text-zinc-900">{r.company_name}</td>
              <td className="px-5 py-2.5 text-zinc-700 text-xs">{r.admin_email || <span className="text-red-700">no admin user</span>}</td>
              <td className="px-5 py-2.5">
                {r.admin_email ? (
                  r.email_verified ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      ✓ verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                      ✗ unverified
                    </span>
                  )
                ) : (
                  <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                    ⚠ missing
                  </span>
                )}
              </td>
              <td className="px-5 py-2.5 text-right">
                {!r.email_verified && r.admin_email && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => resend(r.company_id, r.company_name)}
                    disabled={busyId === r.company_id}
                    data-testid={`resend-${r.company_id}`}
                    className="h-7 px-2 text-xs"
                  >
                    {busyId === r.company_id ? "Sending…" : "Resend"}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Partner API Keys — super-admin management dialog.
 * Mint / list / revoke opaque bearer tokens used by 3rd-party storefronts
 * (e.g. kyoown.com) to create Kanal customer accounts server-to-server.
 * Plaintext token is returned by the backend exactly once at creation.
 * ─────────────────────────────────────────────────────────────────── */
function PartnerKeysButton({ company }) {
  const [open, setOpen] = React.useState(false);
  const [keys, setKeys] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [newKeyName, setNewKeyName] = React.useState("");
  const [minting, setMinting] = React.useState(false);
  const [freshToken, setFreshToken] = React.useState(null); // {name, token}
  const [copied, setCopied] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/companies/${company.id}/partner-keys`);
      setKeys(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load keys");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { if (open) { load(); setFreshToken(null); setNewKeyName(""); } }, [open]);

  const mint = async () => {
    setMinting(true);
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/partner-keys`, {
        name: newKeyName.trim() || `${company.name} partner key`,
      });
      setFreshToken({ name: data.name, token: data.token });
      setNewKeyName("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to mint key");
    } finally { setMinting(false); }
  };

  const revoke = async (keyId, name) => {
    if (!window.confirm(`Revoke "${name}"? Any partner using this token will start getting 401s immediately.`)) return;
    try {
      await api.delete(`/admin/partner-keys/${keyId}`);
      toast.success("Key revoked");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to revoke");
    }
  };

  const copyToken = async () => {
    if (!freshToken?.token) return;
    try {
      await navigator.clipboard.writeText(freshToken.token);
      setCopied(true);
      toast.success("Token copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard access denied — select and copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" title="Partner API keys" data-testid={`partner-keys-${company.id}`}>
          <Key size={15} />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl" data-testid="partner-keys-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>Partner API keys — {company.name}</span>
            <a
              href="/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border shrink-0"
              style={{ color: "#002BF6", borderColor: "rgba(0,43,246,0.3)", background: "rgba(0,43,246,0.05)" }}
              data-testid="view-api-docs-link"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              API Docs
            </a>
          </DialogTitle>
        </DialogHeader>

        {/* Freshly-minted token banner — shown ONCE */}
        {freshToken && (
          <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-4 space-y-2" data-testid="fresh-token-banner">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <Key size={16} weight="bold" /> New key: {freshToken.name}
            </div>
            <p className="text-xs text-amber-800">
              Copy this token now — it will <b>never</b> be shown again. Only its SHA-256 hash is stored server-side.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-white border border-amber-300 px-3 py-2 text-xs font-mono break-all select-all" data-testid="fresh-token-value">
                {freshToken.token}
              </code>
              <Button size="sm" onClick={copyToken} className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" data-testid="copy-token-btn">
                {copied ? <><Check size={14} className="mr-1" /> Copied</> : <><Copy size={14} className="mr-1" /> Copy</>}
              </Button>
            </div>
          </div>
        )}

        {/* Mint new key */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor="new-key-name" className="text-xs uppercase tracking-wider text-zinc-500">Key label</Label>
            <Input
              id="new-key-name"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g. kyoown.com production"
              className="mt-1"
              data-testid="new-key-name-input"
            />
          </div>
          <Button onClick={mint} disabled={minting} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="mint-key-btn">
            {minting ? "Minting…" : "Mint key"}
          </Button>
        </div>

        {/* Existing keys */}
        <div className="border border-zinc-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Prefix</th>
                <th className="px-3 py-2 font-semibold">Calls</th>
                <th className="px-3 py-2 font-semibold">Last used</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-500 text-xs">Loading…</td></tr>
              ) : keys.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-500 text-xs">No keys yet — mint one above.</td></tr>
              ) : keys.map((k) => (
                <tr key={k.id} className="border-b border-zinc-100 last:border-0" data-testid={`key-row-${k.id}`}>
                  <td className="px-3 py-2 font-medium">{k.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-600">{k.prefix}…</td>
                  <td className="px-3 py-2 text-zinc-700">{k.call_count || 0}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2">
                    {k.revoked_at
                      ? <span className="inline-flex rounded-full px-2 py-0.5 text-xs bg-red-50 text-red-700 border border-red-200">Revoked</span>
                      : <span className="inline-flex rounded-full px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!k.revoked_at && (
                      <Button size="sm" variant="ghost" onClick={() => revoke(k.id, k.name)} className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs" data-testid={`revoke-key-${k.id}`}>
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

